import api from './api';
import { Contact, ContactResponse, ContactsResponse } from './types';

/**
 * Contact Service
 * Handles all contact-related API requests
 */
const contactService = {
    /**
     * Get all contacts
     * @returns Promise<Contact[]>
     */
    getAll: async (): Promise<Contact[]> => {
        try {
            const response = await api.get<ContactsResponse>('/contacts');
            return response.contacts || [];
        } catch (error) {
            console.error('Error fetching contacts:', error);
            throw error;
        }
    },

    /**
     * Get contact by public ID
     * @param publicId - Contact public ID
     * @returns Promise<Contact>
     */
    getById: async (publicId: string): Promise<Contact> => {
        try {
            const response = await api.get<ContactResponse>(`/contacts/${publicId}`);
            return response.contact;
        } catch (error) {
            console.error(`Error fetching contact ${publicId}:`, error);
            throw error;
        }
    },

    /**
     * Create new contact
     * @param contactData - Contact data
     * @returns Promise<Contact>
     */
    create: async (contactData: Partial<Contact>): Promise<Contact> => {
        try {
            const response = await api.post<ContactResponse>('/contacts', contactData);
            return response.contact;
        } catch (error) {
            console.error('Error creating contact:', error);
            throw error;
        }
    },

    /**
     * Update contact
     * @param publicId - Contact public ID
     * @param contactData - Contact data to update
     * @returns Promise<Contact>
     */
    update: async (publicId: string, contactData: Partial<Contact>): Promise<Contact> => {
        try {
            const response = await api.put<ContactResponse>(`/contacts/${publicId}`, contactData);
            return response.contact;
        } catch (error) {
            console.error(`Error updating contact ${publicId}:`, error);
            throw error;
        }
    },

    /**
     * Delete contact
     * @param publicId - Contact public ID
     * @returns Promise<void>
     */
    delete: async (publicId: string): Promise<void> => {
        try {
            await api.delete(`/contacts/${publicId}`);
        } catch (error) {
            console.error(`Error deleting contact ${publicId}:`, error);
            throw error;
        }
    }
};

export default contactService;
